import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import { getFavoriteGames } from '../../microservices/casino-preferences';
import { stores } from '../../stores';
import { casino } from '../../stores/casino';
import { casinoPaybackStore } from '../../stores/casino-payback';
import { paybackBooster as paybackBoosterStore } from '../../stores/payback-booster';
import { getStoreValue } from '../../stores/store/utils';
import { isFeatureAvailable } from '../feature';
import { getRoute } from '../router';
import { translate } from '../translate';
import { FEATURE } from '../types';
import { GameLabels } from './game-label';
import {
    CasinoCategory,
    CasinoCategoryCode,
    CasinoCategoryTypes,
    CasinoCustomCategoryIds,
    CasinoGame,
    CasinoRace,
} from './types';

export function mapCasinoCategories(raceGames: CasinoGame[]): void {
    let categories = getStoreValue(casino.categories);
    insertCustomCategories(categories, raceGames);
    const gamesById = getStoreValue(casino.gamesById);

    categories.forEach((category) => {
        category.games = category.games.filter((gameId) => gamesById[gameId]);
    });
    categories = categories.filter((category) => category.games.length);
    casino.categories.set(categories);
    casino.categoriesById.set(keyBy(categories, 'id'));
    casino.categoriesByCode.set(keyBy(categories, 'code'));
    casino.categoriesBySlug.set(keyBy(categories, (category) => getCasinoCategorySlug(category.id)));
}

function insertCustomCategories(categories: CasinoCategory[], raceGames: CasinoGame[]) {
    const categoryCodes = categories.map(({ code }) => code);
    if (isFeatureAvailable(FEATURE.RECENTLY_PLAYED_GAMES)) {
        const recentlyPlayedCategory: CasinoCategory = getRecentlyPlayedCategory();
        const shouldInsertRecentlyPlayed = !categoryCodes.includes(CasinoCategoryCode.RECENTLY_PLAYED);

        if (shouldInsertRecentlyPlayed) {
            categories.push(recentlyPlayedCategory);
        }
    }

    const paybackBoosterCategory = getPaybackBoosterCategory();
    const shouldInsertPaybackBooster =
        paybackBoosterCategory && !categoryCodes.includes(CasinoCategoryCode.PAYBACK_BOOSTER);
    if (paybackBoosterCategory && shouldInsertPaybackBooster) {
        categories.push(paybackBoosterCategory);
    }

    const race = getStoreValue(stores.casinoRace.active.race);
    const raceCategory = getCasinoRaceCategory(race, raceGames);
    const shouldInsertRaceCategory = raceCategory && !categoryCodes.includes(CasinoCategoryCode.RACE);
    if (shouldInsertRaceCategory) {
        categories.push(raceCategory);
    }

    const casinoPaybackCategory = getCasinoPaybackCategory();
    if (casinoPaybackCategory) {
        categories.push(casinoPaybackCategory);
    }

    const favoriteGamesCategory = getFavoriteCategory();
    if (favoriteGamesCategory && !categoryCodes.includes(CasinoCategoryCode.FAVORITE_GAMES)) {
        categories.push(favoriteGamesCategory);
    }
}

export function getFavoriteCategory(): CasinoCategory | null {
    const favoriteGameIds = getStoreValue(casino.userPreferences.favoriteGameIds);

    if (!favoriteGameIds.length) {
        return null;
    }

    return {
        id: CasinoCustomCategoryIds.FAVORITE_GAMES,
        name: 'Favorite games',
        code: CasinoCategoryCode.FAVORITE_GAMES,
        order: 97,
        type: CasinoCategoryTypes.CASINO,
        games: favoriteGameIds,
        isCustom: true,
        filters: [],
    };
}

function getRecentlyPlayedCategory() {
    const recentlyPlayedGames = getStoreValue(casino.recentlyPlayedGames);
    const recentlyPlayedCategory: CasinoCategory = {
        id: CasinoCustomCategoryIds.RECENTLY_PLAYED,
        name: 'Recently played games',
        code: CasinoCategoryCode.RECENTLY_PLAYED,
        order: 98,
        type: CasinoCategoryTypes.CASINO,
        games: recentlyPlayedGames.map((recentlyPlayedGame) => recentlyPlayedGame.id),
        isCustom: true,
        filters: [],
    };
    return recentlyPlayedCategory;
}

function getCasinoRaceCategory(race: CasinoRace, raceGames: CasinoGame[]): CasinoCategory | null {
    if (!race) {
        return null;
    }

    const raceCategory: CasinoCategory = {
        id: CasinoCustomCategoryIds.CASINO_RACE,
        name: 'Casino race games',
        code: CasinoCategoryCode.RACE,
        order: 99,
        type: CasinoCategoryTypes.CASINO,
        games: raceGames.map((raceGame) => raceGame.id),
        isCustom: true,
        filters: [],
    };
    return raceCategory;
}

function getPaybackBoosterCategory(): CasinoCategory | null {
    const [program] = getStoreValue(paybackBoosterStore.programGames);
    const paybackBooster = getStoreValue(paybackBoosterStore.paybackBooster);
    if (!paybackBooster) {
        return null;
    }
    const filteredGames = getStoreValue<CasinoGame[]>(casino.filteredGames);
    const extraPayback = program?.cashbackSettings?.extraPayback;

    if (!extraPayback || (program && Boolean(new Date(program?.periodTo) < new Date())) || isEmpty(paybackBooster)) {
        return null;
    }
    const serverGameIds = Object.keys(extraPayback);

    const promotedGames = program.cashbackSettings.promotedGames;
    if (promotedGames) {
        promotedGames.forEach((promotedGame) => {
            const index = serverGameIds.indexOf(promotedGame);
            if (index > -1) {
                serverGameIds.splice(index, 1);
            }
        });
        serverGameIds.unshift(...promotedGames);
    }
    const paybackBoosterGames = serverGameIds.reduce((prevGameIds: CasinoGame[], gameId) => {
        const game = keyBy(filteredGames, 'serverGameId')[gameId];
        if (game) {
            prevGameIds.push(game);
        }
        return prevGameIds;
    }, []);

    paybackBoosterStore.games.set(paybackBoosterGames);
    const paybackBoosterCategory: CasinoCategory = {
        id: CasinoCustomCategoryIds.PAYBACK_BOOSTER,
        name: 'Payback booster',
        code: CasinoCategoryCode.PAYBACK_BOOSTER,
        order: 100,
        type: CasinoCategoryTypes.CASINO,
        games: paybackBoosterGames.map((paybackBoosterGame) => paybackBoosterGame.id),
        isCustom: true,
        filters: [],
    };
    return paybackBoosterCategory;
}

function getCasinoPaybackCategory(): CasinoCategory | null {
    const casinoPayback = getStoreValue(casinoPaybackStore.casinoPayback);
    const gamesByGroupId = getStoreValue(casino.gamesByGroupId);

    if (!casinoPayback) {
        return null;
    }

    const casinoPaybackGames = casinoPayback?.gameGroupIds
        .map((gameGroupId) => gamesByGroupId[gameGroupId] && gamesByGroupId[gameGroupId])
        .filter(Boolean);

    const casinoPaybackCategory: CasinoCategory = {
        id: CasinoCustomCategoryIds.CASINO_PAYBACK,
        name: 'Casino payback',
        code: CasinoCategoryCode.CASINO_PAYBACK,
        order: 101,
        type: CasinoCategoryTypes.CASINO,
        games: casinoPaybackGames.map((game) => game.id) || [],
        isCustom: true,
        filters: [],
    };

    return casinoPaybackCategory;
}

export function getCasinoCategoryRoute(category: CasinoCategory): string {
    return `${getRoute({ casino: 'casino.slots', live: 'casino.live' }[category.type])}/${translate(
        `casino.category.${category.id}.slug`,
    )}`;
}

export function getCasinoMiniCategoryRoute(category: CasinoCategory): string {
    return `${getRoute('casino.slots')}/mini/${translate(`casino.category.${category.id}.slug`)}`;
}

export function getCasinoCategorySubprovidersRoute(categoryType?): string {
    return `${getRoute({ casino: 'casino.slots', live: 'casino.live' }[categoryType || 'casino'])}/providers`;
}

export function getHiddenGameLabelsByCategory(category: CasinoCategory) {
    return (
        (category &&
            ((category.code === CasinoCategoryCode.NEW_GAMES && [GameLabels.NEW]) ||
                (category.code === CasinoCategoryCode.TOP_20 && [GameLabels.HOT]))) ||
        []
    );
}

export function getCasinoCategoryName(id): string {
    return translate(`${id === -2 ? 'payback booster' : 'name'}`, `casino.category.${id}`);
}

const getCasinoCategorySlug = (id): string => translate('slug', `casino.category.${id}`);

export function getCategoryTitle(categoryCode: string): string {
    const categoriesByCode = getStoreValue(casino.categoriesByCode);
    const category = categoriesByCode[categoryCode];

    const isRecentlyPlayed = categoryCode === CasinoCategoryCode.RECENTLY_PLAYED;

    const context = category?.isCustom ? 'ui.casino' : `casino.category.${category?.id}`;
    let key = isRecentlyPlayed ? 'recently-played' : 'name';

    if (category?.isCustom && !isRecentlyPlayed) {
        key = category?.name;
    }

    return translate(key, context);
}

export const lobbyLauncherTypeToCategory = {
    baccarat: 'live-baccarat',
    roulette: 'live-roulette',
    blackjack: 'live-blackjack',
    gameshow: 'gameshows',
} as const;

export async function loadFavoriteGames() {
    const isAuthenticated = getStoreValue(stores.isAuthenticated);
    const gamesByGroupId = getStoreValue<{ [key: number]: CasinoGame }>(casino.gamesByGroupId);
    const favoriteGamesGroupIds = isAuthenticated ? await getFavoriteGames() : [];
    const favoriteGameIds = favoriteGamesGroupIds.map((gameGroupId) => gamesByGroupId[gameGroupId]?.id).filter(Boolean);

    casino.userPreferences.favoriteGameIds.set(favoriteGameIds);
}
